import { parseLocateInjectionCsv } from './parseLocateInjectionCsv';
import type { LocateInjectionCsvParseResult } from './parseLocateInjectionCsv';
import type { LocatesInjectionAction } from './state/actions';
import { locatesInjectionReducer } from './state/reducer';
import { createInitialLocatesInjectionState } from './state/types';
import type {
    CsvLocateInjectionDraft,
    LocatesInjectionMode,
    LocatesInjectionState,
    SingleLocateInjectionDraft,
} from './state/types';
import {
    buildCsvInjectionParams,
    CsvLocateInjectionSelectionSchema,
    SingleLocateInjectionDraftSchema,
} from './validation';
import { computed, createModel, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { MutationObserver } from '@tanstack/query-core';
import type { QueryClient } from '@tanstack/query-core';
import { requestIdHeader } from '@thinkalpha/common/contracts/x-headers.js';
import { doesAccessGrantAllowRequest } from '@thinkalpha/common/util/permissions.js';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { LocateOrder } from '@thinkalpha/ui-types/contracts/locates/orders';
import type { LocateAccount } from '@thinkalpha/ui-types/contracts/locates/platforms';
import type { LocateProvider } from '@thinkalpha/ui-types/contracts/locates/providers';
import { isAxiosError } from 'axios';
import { modelDisposableStack } from 'src/lib/signals/modelDisposableStack';
import { InjectLocatesParamsSchema } from 'src/queries/locatesV2/injection';
import type { InjectLocatesParams } from 'src/queries/locatesV2/injection';
import { injectLocatesMutation } from 'src/queries/locatesV2Mutations';
import type { z } from 'zod';

type SingleField = keyof SingleLocateInjectionDraft;
type CsvSelectionField = 'accountId' | 'providerId';

interface CsvParseCompletion {
    file: { name: string; size: number };
    result: LocateInjectionCsvParseResult;
}

class CsvParseOperation implements Disposable {
    readonly #abortController = new AbortController();
    readonly #file: File;
    #completion: Promise<CsvParseCompletion | null> | undefined;

    constructor(file: File) {
        this.#file = file;
    }

    run(): Promise<CsvParseCompletion | null> {
        return (this.#completion ??= this.#parse());
    }

    async #parse(): Promise<CsvParseCompletion | null> {
        if (this.#abortController.signal.aborted) return null;
        let result: LocateInjectionCsvParseResult;
        try {
            result = this.#file.name.toLocaleLowerCase().endsWith('.csv')
                ? parseLocateInjectionCsv(await this.#file.text())
                : {
                      rows: [],
                      errors: [{ rowIndex: 0, message: 'Select a CSV file.' }],
                  };
        } catch {
            result = {
                rows: [],
                errors: [{ rowIndex: 0, message: 'The CSV file could not be read.' }],
            };
        }

        if (this.#abortController.signal.aborted) return null;
        return {
            file: { name: this.#file.name, size: this.#file.size },
            result,
        };
    }

    [Symbol.dispose](): void {
        this.#abortController.abort();
    }
}

export interface LocatesInjectionModelInit {
    instanceId: string;
    logger: Pick<Logger, 'info' | 'warn' | 'error'>;
    queryClient: QueryClient;
    accounts: ReadonlySignal<ReadonlyArray<LocateAccount>>;
    providers: ReadonlySignal<ReadonlyArray<LocateProvider>>;
    preferredAccountIds: ReadonlySignal<ReadonlyArray<string>>;
    injectLocates?: (params: InjectLocatesParams) => Promise<LocateOrder[]>;
    onInjected: (orders: LocateOrder[]) => void | Promise<void>;
    onSuccess?: (count: number) => void;
    onFailure?: (error: unknown) => void;
}

function zodFieldErrors<TField extends string>(result: z.ZodSafeParseResult<unknown>): Partial<Record<TField, string>> {
    if (result.success) return {};
    const errors: Partial<Record<TField, string>> = {};
    for (const issue of result.error.issues) {
        const field = issue.path[0];
        if (typeof field === 'string' && errors[field as TField] === undefined) {
            errors[field as TField] = issue.message;
        }
    }
    return errors;
}

function errorMessage(error: unknown): string {
    if (error instanceof Error && error.message.trim()) return error.message;
    return 'Unable to inject locates. Please try again.';
}

function injectionErrorDetails(error: unknown): {
    errorMessage: string;
    errorCode?: string;
    status?: number;
    requestId?: string;
} {
    if (!isAxiosError(error)) {
        return {
            errorMessage: errorMessage(error),
            ...(error instanceof Error ? { errorCode: error.name } : {}),
        };
    }

    const requestHeaderRequestId = error.config?.headers?.[requestIdHeader];
    const requestId = typeof requestHeaderRequestId === 'string' ? requestHeaderRequestId : undefined;

    return {
        errorMessage: error.message,
        errorCode: error.code ?? error.name,
        status: error.response?.status,
        ...(requestId ? { requestId } : {}),
    };
}

function modeLabel(mode: LocatesInjectionMode): string {
    return mode === 'csv' ? 'CSV' : 'single';
}

export const LocatesInjectionModel = createModel((init: LocatesInjectionModelInit) => {
    const disposableStack = modelDisposableStack();
    const activeCsvParseOperation = disposableStack.use(new DisposableBox<CsvParseOperation>());
    const state = signal<LocatesInjectionState>(createInitialLocatesInjectionState());
    const mutation = new MutationObserver<LocateOrder[], Error, InjectLocatesParams>(
        init.queryClient,
        injectLocatesMutation(init.injectLocates),
    );

    function dispatch(action: LocatesInjectionAction): void {
        state.value = locatesInjectionReducer(state.peek(), action);
    }

    const eligibleAccounts = computed(() =>
        init.accounts.value.filter(
            (account) => account.canInject && doesAccessGrantAllowRequest(account.permissions.effectiveAccess, 'admin'),
        ),
    );
    const injectableProviders = computed(() => init.providers.value.filter((provider) => provider.isInjectable));
    const canInject = computed(() => eligibleAccounts.value.length > 0 && injectableProviders.value.length > 0);

    const mode = computed(() => state.value.mode);
    const singleDraft = computed(() => state.value.single);
    const csvDraft = computed(() => state.value.csv);
    const isParsing = computed(() => state.value.status === 'parsing');
    const isSubmitting = computed(() => state.value.status === 'submitting');
    const submissionError = computed(() => state.value.submissionError);

    const singleValidation = computed(() => SingleLocateInjectionDraftSchema.safeParse(state.value.single));
    const csvSelectionValidation = computed(() =>
        CsvLocateInjectionSelectionSchema.safeParse({
            accountId: state.value.csv.accountId,
            providerId: state.value.csv.providerId,
        }),
    );

    const singleFieldErrors = computed<Partial<Record<SingleField, string>>>(() => {
        const errors = zodFieldErrors<SingleField>(singleValidation.value);
        const draft = state.value.single;
        if (draft.accountId && !eligibleAccounts.value.some((account) => account.id === draft.accountId)) {
            errors.accountId = 'Account is not eligible for injection';
        }
        if (draft.providerId && !injectableProviders.value.some((provider) => provider.id === draft.providerId)) {
            errors.providerId = 'Provider is not available for injection';
        }
        return errors;
    });

    const csvSelectionErrors = computed<Partial<Record<CsvSelectionField, string>>>(() => {
        const errors = zodFieldErrors<CsvSelectionField>(csvSelectionValidation.value);
        const draft = state.value.csv;
        if (draft.accountId && !eligibleAccounts.value.some((account) => account.id === draft.accountId)) {
            errors.accountId = 'Account is not eligible for injection';
        }
        if (draft.providerId && !injectableProviders.value.some((provider) => provider.id === draft.providerId)) {
            errors.providerId = 'Provider is not available for injection';
        }
        return errors;
    });

    const canSubmitSingle = computed(
        () =>
            state.value.status === 'idle' &&
            singleValidation.value.success &&
            Object.keys(singleFieldErrors.value).length === 0,
    );
    const canSubmitCsv = computed(() => {
        const parsed = state.value.csv.parseResult;
        return (
            state.value.status === 'idle' &&
            csvSelectionValidation.value.success &&
            Object.keys(csvSelectionErrors.value).length === 0 &&
            parsed !== null &&
            parsed.rows.length > 0 &&
            parsed.errors.length === 0
        );
    });
    const canSubmit = computed(() => (state.value.mode === 'single' ? canSubmitSingle.value : canSubmitCsv.value));

    effect(() => {
        if (state.value.mode === null) return;
        const accounts = eligibleAccounts.value;
        const providers = injectableProviders.value;
        const activeDraft = state.value.mode === 'single' ? state.value.single : state.value.csv;
        const accountId = accounts.some((account) => account.id === activeDraft.accountId)
            ? activeDraft.accountId
            : (accounts[0]?.id ?? '');
        const providerId = providers.some((provider) => provider.id === activeDraft.providerId)
            ? activeDraft.providerId
            : (providers[0]?.id ?? '');
        dispatch({ type: 'RECONCILE_SELECTIONS', accountId, providerId });
    });

    function open(nextMode: LocatesInjectionMode): boolean {
        const accounts = eligibleAccounts.peek();
        const providers = injectableProviders.peek();
        if (!canInject.peek()) {
            init.logger.warn(`Could not open ${modeLabel(nextMode)} locate injection dialog`, {
                operation: 'locatesInjection',
                stage: 'openRejected',
                instanceId: init.instanceId,
                mode: nextMode,
                eligibleAccountCount: accounts.length,
                injectableProviderCount: providers.length,
            });
            return false;
        }
        const preferred = accounts.filter((account) => init.preferredAccountIds.peek().includes(account.id));
        const accountId = (preferred.length === 1 ? preferred[0] : accounts[0])?.id ?? '';
        const providerId = providers[0]?.id ?? '';
        activeCsvParseOperation.clear();
        dispatch({ type: 'OPEN', mode: nextMode, accountId, providerId });
        init.logger.info(`Opened ${modeLabel(nextMode)} locate injection dialog`, {
            operation: 'locatesInjection',
            stage: 'opened',
            instanceId: init.instanceId,
            mode: nextMode,
            accountId,
            providerId,
            eligibleAccountCount: accounts.length,
            injectableProviderCount: providers.length,
        });
        return true;
    }

    function close(): void {
        if (state.peek().status === 'submitting') return;
        activeCsvParseOperation.clear();
        const current = state.peek();
        if (current.mode !== null) {
            init.logger.info(`Closed ${modeLabel(current.mode)} locate injection dialog`, {
                operation: 'locatesInjection',
                stage: 'closed',
                instanceId: init.instanceId,
                mode: current.mode,
                hadSubmissionError: current.submissionError !== null,
            });
        }
        dispatch({ type: 'CLOSE' });
    }

    function updateSingle(patch: Partial<SingleLocateInjectionDraft>): void {
        dispatch({
            type: 'UPDATE_SINGLE',
            patch: {
                ...patch,
                ...(patch.symbol !== undefined ? { symbol: patch.symbol.trim().toUpperCase() } : {}),
            },
        });
    }

    function updateCsv(patch: Partial<Pick<CsvLocateInjectionDraft, 'accountId' | 'providerId'>>): void {
        dispatch({ type: 'UPDATE_CSV', patch });
    }

    async function selectCsvFile(file: File): Promise<void> {
        if (state.peek().status === 'submitting') return;
        const operation = new CsvParseOperation(file);
        activeCsvParseOperation.next(operation);
        dispatch({ type: 'START_CSV_PARSE' });
        try {
            const completion = await operation.run();
            if (!completion) return;
            const { file: parsedFile, result } = completion;

            dispatch({
                type: 'FINISH_CSV_PARSE',
                file: parsedFile,
                result,
            });
            const context = {
                operation: 'locatesInjection',
                stage: 'csvParsed',
                instanceId: init.instanceId,
                mode: 'csv',
                fileSize: parsedFile.size,
                validRowCount: result.rows.length,
                errorCount: result.errors.length,
            };
            if (result.errors.length > 0) {
                init.logger.info(
                    `Parsed locate injection CSV with ${result.rows.length} valid ${result.rows.length === 1 ? 'row' : 'rows'} and ${result.errors.length} ${result.errors.length === 1 ? 'error' : 'errors'}`,
                    context,
                );
            } else {
                init.logger.info(
                    `Parsed locate injection CSV with ${result.rows.length} valid ${result.rows.length === 1 ? 'row' : 'rows'}`,
                    context,
                );
            }
        } finally {
            if (activeCsvParseOperation.current === operation) activeCsvParseOperation.release();
        }
    }

    function buildSubmission(): z.ZodSafeParseResult<InjectLocatesParams> {
        const current = state.peek();
        if (current.mode === 'single') {
            return SingleLocateInjectionDraftSchema.safeParse(current.single);
        }
        if (current.mode === 'csv' && current.csv.parseResult) {
            return buildCsvInjectionParams({
                accountId: current.csv.accountId,
                providerId: current.csv.providerId,
                rows: current.csv.parseResult.rows,
            });
        }
        return InjectLocatesParamsSchema.safeParse({});
    }

    async function submit(): Promise<boolean> {
        const current = state.peek();
        if (current.status !== 'idle') {
            init.logger.warn('Ignored locate injection submission while another operation was in progress', {
                operation: 'locatesInjection',
                stage: 'submitIgnored',
                instanceId: init.instanceId,
                mode: current.mode,
                status: current.status,
            });
            return false;
        }
        const submission = buildSubmission();
        if (!submission.success || !canSubmit.peek()) {
            init.logger.warn('Blocked invalid locate injection submission', {
                operation: 'locatesInjection',
                stage: 'submitBlocked',
                instanceId: init.instanceId,
                mode: current.mode,
                validationIssueCount: submission.success ? 0 : submission.error.issues.length,
            });
            dispatch({
                type: 'FAIL_SUBMIT',
                error: 'Review the injection details and correct the highlighted fields.',
            });
            return false;
        }

        dispatch({ type: 'START_SUBMIT' });
        mutation.reset();
        const startedAt = performance.now();
        const { accountId, receipts } = submission.data;
        const providerIds = receipts
            .values()
            .map((receipt) => receipt.providerId)
            .unique()
            .toArray();
        init.logger.info(`Submitting ${receipts.length} locate injection receipt${receipts.length === 1 ? '' : 's'}`, {
            operation: 'locatesInjection',
            stage: 'submitted',
            instanceId: init.instanceId,
            mode: current.mode,
            accountId,
            providerIds,
            receiptCount: receipts.length,
        });
        let injections: LocateOrder[];
        try {
            injections = await mutation.mutate(submission.data);
            const successContext = {
                operation: 'locatesInjection',
                stage: 'succeeded',
                instanceId: init.instanceId,
                mode: current.mode,
                accountId,
                providerIds,
                receiptCount: receipts.length,
                injectionCount: injections.length,
                outcome: injections.length === 0 ? 'empty' : 'success',
                durationMs: Math.round(performance.now() - startedAt),
            };
            if (injections.length === 0) {
                init.logger.warn(
                    `Locate injection request succeeded but returned no injections for ${receipts.length} receipt${receipts.length === 1 ? '' : 's'}`,
                    successContext,
                );
            } else {
                init.logger.info(
                    `Locate injection succeeded with ${injections.length} returned injection${injections.length === 1 ? '' : 's'}`,
                    successContext,
                );
            }
        } catch (error) {
            const details = injectionErrorDetails(error);
            init.logger.error(
                `Locate injection failed for ${receipts.length} receipt${receipts.length === 1 ? '' : 's'}`,
                {
                    operation: 'locatesInjection',
                    stage: 'failed',
                    instanceId: init.instanceId,
                    mode: current.mode,
                    accountId,
                    providerIds,
                    receiptCount: receipts.length,
                    outcome: 'failure',
                    durationMs: Math.round(performance.now() - startedAt),
                    ...details,
                },
            );
            dispatch({ type: 'FAIL_SUBMIT', error: errorMessage(error) });
            init.onFailure?.(error);
            return false;
        }

        try {
            await init.onInjected(injections);
        } catch (error) {
            init.logger.error('Could not apply injected locates to the local results grid', {
                operation: 'locatesInjection',
                stage: 'applyFailed',
                instanceId: init.instanceId,
                mode: current.mode,
                injectionCount: injections.length,
                ...injectionErrorDetails(error),
            });
        }
        dispatch({ type: 'SUCCEED_SUBMIT' });
        init.onSuccess?.(injections.length);
        return true;
    }

    return {
        mode,
        singleDraft,
        csvDraft,
        eligibleAccounts,
        injectableProviders,
        canInject,
        isParsing,
        isSubmitting,
        submissionError,
        singleFieldErrors,
        csvSelectionErrors,
        canSubmitSingle,
        canSubmitCsv,
        canSubmit,
        open,
        close,
        updateSingle,
        updateCsv,
        selectCsvFile,
        submit,
    };
});

export type LocatesInjectionModel = InstanceType<typeof LocatesInjectionModel>;
export type {
    CsvLocateInjectionDraft,
    LocatesInjectionMode,
    LocatesInjectionState,
    SingleLocateInjectionDraft,
} from './state/types';
